import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  BeakerIcon,
  ClockIcon,
  CogIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

interface FeatureProps {
  icon: any;
  title: string;
  description: string;
  delay: number;
  index: number;
}

const Feature = ({ icon: Icon, title, description, delay, index }: FeatureProps) => (
  <Link to="/services" className="block">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true }}
      className="relative group cursor-pointer"
    >
      {/* Animated background gradient */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay }}
        className="absolute inset-0 bg-gradient-to-br from-[#FF7066]/10 via-secondary/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500"
        style={{
          background: `radial-gradient(circle at ${index % 2 === 0 ? 'top left' : 'bottom right'}, rgba(255, 112, 102, 0.1), rgba(var(--secondary-rgb), 0.1), transparent)`,
        }}
      />
      
      {/* Card content */}
      <div className="relative flex flex-col h-full p-8 bg-white/80 backdrop-blur-md rounded-2xl border border-primary/10 hover:border-[#FF7066]/20 transition-all duration-300 hover:shadow-xl hover:shadow-[#FF7066]/5 group-hover:scale-[1.02]">
        {/* Icon container with animated background */}
        <motion.div 
          whileHover={{ scale: 1.1 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
          className="mb-6 relative"
        >
          <div className="absolute inset-0 bg-[#FF7066]/10 rounded-2xl blur-xl transform group-hover:scale-110 transition-transform duration-300" />
          <div className="relative p-4 rounded-2xl bg-gradient-to-br from-primary/10 to-[#FF7066]/10 border border-[#FF7066]/20">
            <Icon className="w-8 h-8 text-primary group-hover:text-[#FF7066] transition-colors duration-300" />
          </div>
        </motion.div>
        
        <h3 className="text-xl font-bold text-primary mb-3 group-hover:text-[#FF7066] transition-colors duration-300">{title}</h3>
        <p className="text-secondary/80 group-hover:text-secondary transition-colors duration-300">{description}</p>
        
        {/* Decorative elements */}
        <div className="absolute top-4 right-4 w-20 h-20 bg-primary/5 rounded-full blur-2xl group-hover:bg-[#FF7066]/5 transition-colors duration-300" />
        <div className="absolute bottom-4 left-4 w-16 h-16 bg-secondary/5 rounded-full blur-2xl group-hover:bg-[#FF7066]/5 transition-colors duration-300" />
        
        {/* Learn more indicator */}
        <div className="mt-4 text-primary font-medium group-hover:text-[#FF7066] transition-colors duration-300 flex items-center gap-1">
          <span>Learn more</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </motion.div>
  </Link>
);

const WhyChooseUs = () => {
  const features = [
    {
      icon: BeakerIcon,
      title: "Cutting-Edge Technology",
      description: " latest AI advancements and stay ahead of industry trends.",
    },
    {
      icon: UserGroupIcon,
      title: "Expert Team",
      description: "Seasoned AI specialists with proven AI Software Development expertise.",
    },
    {
      icon: ShieldCheckIcon,
      title: "Security First",
      description: "Your data security is our priority, with enterprise-grade protection measures.",
    },
    {
      icon: ClockIcon,
      title: "Rapid Development",
      description: "Quick turnaround times without compromising on quality or performance.",
    },
    {
      icon: LightBulbIcon,
      title: "Innovative Solutions",
      description: "Custom-tailored AI solutions that address your unique business challenges.",
    },
    {
      icon: CogIcon,
      title: "Seamless Integration",
      description: "Easy integration with your existing systems and workflows.",
    },
  ];

  return (
    <section id="why-choose-us" className="py-24 relative overflow-hidden bg-gradient-to-b from-white via-[#FF7066]/5 to-white">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.03]" />
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(255, 112, 102, 0.03), transparent 70%)',
        }}
      />
      
      {/* Floating Orbs */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        className="absolute top-20 left-20 w-64 h-64 bg-[#FF7066]/10 rounded-full blur-3xl"
      />
      <motion.div
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          repeatType: "reverse",
          delay: 2,
        }}
        className="absolute bottom-20 right-20 w-64 h-64 bg-[#FF7066]/10 rounded-full blur-3xl"
      />
      
      <div className="container relative z-10 px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-[#FF7066]/10 to-secondary/10 text-[#FF7066] text-sm font-medium border border-[#FF7066]/10 mb-6"
          >
            Why Choose BetterByAI
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-primary mb-6"
          >
            Experience the Future of
            <span className="bg-gradient-to-r from-[#FF7066] to-secondary bg-clip-text text-transparent"> AI Innovation</span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl text-secondary"
          >
            We combine cutting-edge technology with deep expertise to deliver AI solutions that transform businesses.
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-24">
          {features.map((feature, index) => (
            <Feature
              key={feature.title}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              delay={0.2 + index * 0.1}
              index={index}
            />
          ))}
        </div>

        {/* Commitment Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#FF7066]/5 via-transparent to-secondary/5 rounded-3xl" />
          <div className="relative bg-white/50 backdrop-blur-sm rounded-3xl border border-[#FF7066]/10 p-12 hover:shadow-xl hover:shadow-[#FF7066]/5 transition-all duration-300">
            <div className="max-w-4xl mx-auto text-center">
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                viewport={{ once: true }}
                className="text-3xl font-bold text-primary mb-6"
              >
                Our Commitment to Excellence
              </motion.h3>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                viewport={{ once: true }}
                className="text-xl text-secondary mb-8"
              >
                As a forward-thinking startup, we're committed to delivering exceptional AI solutions that drive real business value. Our agile approach allows us to adapt quickly to your needs while maintaining the highest standards of quality and innovation.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                viewport={{ once: true }}
                className="flex flex-wrap justify-center gap-6"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#FF7066]" />
                  <span className="text-secondary">Personalised Solutions</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#FF7066]" />
                  <span className="text-secondary">Dedicated Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#FF7066]" />
                  <span className="text-secondary">Continuous Innovation</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#FF7066]" />
                  <span className="text-secondary">Future-Ready Technology</span>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;